import { request } from 'utils';

import {
  FETCH_SEO_META_REQUEST,
  FETCH_SEO_META_SUCCESS,
  FETCH_SEO_META_FAILURE,
  FETCH_SEO_LINKS_BLOCKS_REQUEST,
  FETCH_SEO_LINKS_BLOCKS_SUCCESS,
  FETCH_SEO_LINKS_BLOCKS_FAILURE,
  FETCH_SEO_TEXTS_BLOCKS_REQUEST,
  FETCH_SEO_TEXTS_BLOCKS_SUCCESS,
  FETCH_SEO_TEXTS_BLOCKS_FAILURE,
  FETCH_BREADCRUMBS_REQUEST,
  FETCH_BREADCRUMBS_SUCCESS,
  FETCH_BREADCRUMBS_FAILURE,
} from 'actionTypes';

import { seoLinks as schema, breadcrumbs as breadcrumbsSchema } from 'schemas';

import {
  makeGetSeoUrl,
  makeGetSeoMeta,
  getBreadcrumbs,
  makeGetSeoLinksBlocks,
  makeGetSeoTextsBlocks,
} from 'selectors';
import { normalize } from 'normalizr';

const getSeoUrl = makeGetSeoUrl();
const getSeoMeta = makeGetSeoMeta();
const getSeoLinksBlocks = makeGetSeoLinksBlocks();
const getSeoTextsBlocks = makeGetSeoTextsBlocks();

const getShouldFetchSeoMeta = (state, props) => {
  const { isFetching, isLoaded } = getSeoMeta(state, props);

  return !(isFetching || isLoaded);
};

export const fetchSeoMeta = props => async (dispatch, getState) => {
  const state = getState();

  if (!getShouldFetchSeoMeta(state, props)) {
    return true;
  }

  const url = getSeoUrl(state, props);

  dispatch({
    type: FETCH_SEO_META_REQUEST,
    payload: { url },
  });

  const requestURL = `${process.env.APP_SEO_META_API_ROOT}?url=${url}`;

  try {
    const {
      data: {
        title: metaTitle,
        description: metaDescription,
        h1: pageTitle,
        h2: pageDescription,
        text: metaText,
      },
    } = await request({ url: requestURL });

    return dispatch({
      type: FETCH_SEO_META_SUCCESS,
      payload: {
        url,
        metaTitle,
        metaDescription,
        pageTitle,
        pageDescription,
        metaText,
      },
    });
  } catch (error) {
    return dispatch({
      type: FETCH_SEO_META_FAILURE,
      payload: { url },
    });
  }
};

const getShouldFetchSeoLinksBlocks = (state, props) => {
  const { isFetching, isLoaded } = getSeoLinksBlocks(state, props);

  return !(isFetching || isLoaded);
};

export const fetchSeoLinksBlocks = props => async (dispatch, getState) => {
  const state = getState();

  if (!getShouldFetchSeoLinksBlocks(state, props)) {
    return true;
  }

  const url = getSeoUrl(state, props);

  dispatch({
    type: FETCH_SEO_LINKS_BLOCKS_REQUEST,
    payload: { url },
  });

  const requestURL = `${process.env.APP_SEO_LINKS_BLOCKS_API_ROOT}?url=${process.env.APP_ROUTER_BASENAME}${props.location.pathname}&locale=${props.intl.locale}`;

  try {
    const { data } = await request({ url: requestURL });

    const { entities, result: ids } =
      !data || (Array.isArray(data) && !data.length)
        ? {}
        : normalize(data, schema);

    return dispatch({
      type: FETCH_SEO_LINKS_BLOCKS_SUCCESS,
      payload: {
        url,
        entities: entities || {},
        ids: ids || [],
      },
    });
  } catch (error) {
    return dispatch({
      type: FETCH_SEO_LINKS_BLOCKS_FAILURE,
      payload: { url },
    });
  }
};

const getShouldFetchSeoTexts = (state, props) => {
  const { isFetching, isLoaded } = getSeoTextsBlocks(state, props);

  return !(isFetching || isLoaded);
};

const fetchSeoTextsBlocksMenu = async (data, locale) => {
  if (!data.length) {
    return [];
  }

  try {
    const { id } = data[0];

    const requestUrl = `${process.env.APP_SEO_TEXTS_BLOCKS_MENU_API_ROOT}?id=${id}&locale=${locale}`;
    const menu = await request({ url: requestUrl });

    return menu;
  } catch (error) {
    return [];
  }
};

export const fetchSeoTextsBlocks = props => async (dispatch, getState) => {
  const state = getState();

  if (!getShouldFetchSeoTexts(state, props)) {
    return true;
  }

  const url = getSeoUrl(state, props);

  dispatch({
    type: FETCH_SEO_TEXTS_BLOCKS_REQUEST,
    payload: { url },
  });

  const requestURL = `${
    process.env.APP_SEO_TEXTS_BLOCKS_API_ROOT
  }?url=${encodeURIComponent(
    `https://minfin.com.ua${process.env.APP_ROUTER_BASENAME}${props.location.pathname}`
  )}&locale=${props.intl.locale}`;

  try {
    const response = await request({ url: requestURL });
    const menu = await fetchSeoTextsBlocksMenu(response, props.intl.locale);

    return dispatch({
      type: FETCH_SEO_TEXTS_BLOCKS_SUCCESS,
      payload: {
        url,
        items: response,
        menu,
      },
    });
  } catch (error) {
    return dispatch({
      type: FETCH_SEO_TEXTS_BLOCKS_FAILURE,
      payload: { url },
    });
  }
};

const getShouldFetchBreadcrumbs = (state, props) => {
  const { isFetching, isLoaded } = getBreadcrumbs(state, props);

  return !(isFetching || isLoaded);
};

export const fetchBreadcrumbs = props => async (dispatch, getState) => {
  const state = getState();
  const { location, intl } = props;

  if (!getShouldFetchBreadcrumbs(state, props)) {
    return true;
  }

  const url = getSeoUrl(state, props);
  const breadcrumbsUrl = `${process.env.APP_ROUTER_BASENAME}${location.pathname}`;

  dispatch({
    type: FETCH_BREADCRUMBS_REQUEST,
    payload: { url },
  });

  try {
    const { data } = await request({
      url: process.env.APP_BREADCRUMBS_API_ROOT,
      params: {
        url: breadcrumbsUrl,
        locale: intl.locale,
      },
    });

    const {
      entities: { breadcrumbs: entities = {} } = {},
      result: ids = [],
    } = normalize(data.links || [], breadcrumbsSchema);

    const items = ids.map(id => entities[id]).filter(Boolean);

    return dispatch({
      type: FETCH_BREADCRUMBS_SUCCESS,
      payload: {
        url,
        items,
      },
    });
  } catch (error) {
    return dispatch({
      type: FETCH_BREADCRUMBS_FAILURE,
      payload: { url },
    });
  }
};
